import React from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigateWithNewTab } from '../hooks/useNavigateWithNewTab';
import AgencySwitcher from './AgencySwitcher';
import {
  Home,
  Users,
  DollarSign,
  ClipboardList,
  Shield,
  BarChart2,
  FileText,
  TestTube,
  UsersIcon,
  TrendingUp,
  CheckCircle,
  Code,
  Target,
  BookOpen,
  SlidersHorizontal,
  Settings2,
  Inbox,
  KeyRound,
  FileCheck,
  FileOutput,
  FileSpreadsheet,
  Wrench,
  DatabaseBackup,
  BadgeCheck,
  Wallet,
  FileBarChart,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { visibleAdminSections } from '../constants/adminSections';
import { FeatureFlag, PermissionSlug } from '../constants/featureFlags';
import { usePayrollAccess } from './payroll/usePayrollAccess';

type SidebarVariant = 'rogerdoc' | 'default';

interface SidebarPresentation {
  /** Render expanded regardless of the desktop collapse state (mobile drawer). */
  forceExpanded: boolean;
  /** Called after a menu item navigates (used to close the mobile drawer). */
  onNavigate?: () => void;
}

const SidebarPresentationContext = React.createContext<SidebarPresentation>({
  forceExpanded: false,
});

interface SidebarMenuItemProps {
  icon: React.ReactNode;
  label: string;
  path: string;
  /** Override the path used for active-state matching (defaults to `path`). */
  activePath?: string;
  variant?: SidebarVariant;
}

interface CollapsedTooltipProps {
  label: string;
  collapsed: boolean;
  children: React.ReactElement;
}

const CollapsedTooltip: React.FC<CollapsedTooltipProps> = ({
  label,
  collapsed,
  children,
}) => {
  if (!collapsed) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side='right'>{label}</TooltipContent>
    </Tooltip>
  );
};

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  icon,
  label,
  path,
  activePath,
  variant = 'default',
}) => {
  const navigateTo = useNavigateWithNewTab();
  const location = useLocation();
  const { forceExpanded, onNavigate } = React.useContext(
    SidebarPresentationContext
  );
  const isCollapsed =
    useStore(state => state.isSidebarCollapsed) && !forceExpanded;
  const isActive = location.pathname.startsWith(activePath ?? path);

  const layoutClass = `flex items-center ${isCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2`;
  const stateClass =
    variant === 'rogerdoc'
      ? isActive
        ? 'bg-accent/50 text-primary font-medium'
        : 'hover:bg-accent/30 text-foreground'
      : `font-medium mb-1 ${isActive ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'}`;

  return (
    <CollapsedTooltip label={label} collapsed={isCollapsed}>
      <button
        onClick={e => {
          navigateTo(path)(e);
          onNavigate?.();
        }}
        className={`${layoutClass} ${stateClass}`}
      >
        {icon}
        {!isCollapsed && <span className='text-sm font-normal'>{label}</span>}
      </button>
    </CollapsedTooltip>
  );
};

interface SidebarProps {
  /** Mobile drawer rendering: always expanded, closes on navigation. */
  mobile?: boolean;
  /** Called after a menu item navigates (used to close the mobile drawer). */
  onNavigate?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobile = false, onNavigate }) => {
  const {
    user,
    hasPermission,
    hasAnyPermission,
    hasFeatureFlag,
    isSidebarCollapsed: isDesktopCollapsed,
  } = useStore(
    useShallow(state => ({
      user: state.user,
      hasPermission: state.hasPermission,
      hasAnyPermission: state.hasAnyPermission,
      hasFeatureFlag: state.hasFeatureFlag,
      isSidebarCollapsed: state.isSidebarCollapsed,
    }))
  );
  const isSidebarCollapsed = isDesktopCollapsed && !mobile;
  const presentation = React.useMemo(
    () => ({ forceExpanded: mobile, onNavigate }),
    [mobile, onNavigate]
  );
  const hasPayrollAccess = usePayrollAccess();
  const hasAdvancedReportingAccess =
    hasFeatureFlag(FeatureFlag.ADVANCED_REPORTING, 'workos') &&
    hasAnyPermission([
      PermissionSlug.REPORTS_VISITS,
      PermissionSlug.REPORTS_EPISODES,
      PermissionSlug.REPORTS_PATIENTS,
      PermissionSlug.REPORTS_CLAIMS,
      PermissionSlug.REPORTS_USER_LICENSES,
    ]);
  const hasReportsAccess =
    hasAdvancedReportingAccess ||
    hasAnyPermission([
      PermissionSlug.REPORTS_ACCESS,
      PermissionSlug.ROGER_EMR_REPORTS_ACCESS,
    ]);
  // We are using is_rogeremr_enabled to block access to the sidebar items that are only
  // available for agencies actually using RogerEMR.
  const rogerEmrEnabled = user?.agency?.is_rogeremr_enabled === true;

  return (
    <SidebarPresentationContext.Provider value={presentation}>
      <aside className='relative flex h-full min-h-0 w-full flex-col transition-all duration-300'>
        {/* Header */}
        <div className='flex h-16 shrink-0 items-center gap-2 border-b pl-3 pr-2'>
          <div className='h-10 w-10 rounded-xl bg-primary flex items-center justify-center text-white text-2xl font-bold shrink-0'>
            R
          </div>
          {!isSidebarCollapsed && (
            <div className='min-w-0 flex-1'>
              <div className='font-semibold whitespace-nowrap'>Roger</div>
              <AgencySwitcher />
            </div>
          )}
        </div>

        {/* Menu */}
        <TooltipProvider delayDuration={300}>
          <nav className='min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-2 py-3'>
            {/* RogerDoc Dashboard Section */}
            {hasPermission(PermissionSlug.ROGERDOC_DASHBOARD_MANAGE) && (
              <>
                <SidebarMenuItem
                  icon={<BarChart2 className='h-4 w-4' />}
                  label='Overview'
                  path='/rogerdoc/overview'
                  variant='rogerdoc'
                />
                <SidebarMenuItem
                  icon={<UsersIcon className='h-4 w-4' />}
                  label='Users'
                  path='/rogerdoc/users'
                  variant='rogerdoc'
                />
                <SidebarMenuItem
                  icon={<TrendingUp className='h-4 w-4' />}
                  label='Usage'
                  path='/rogerdoc/usage'
                  variant='rogerdoc'
                />
              </>
            )}

            {hasPermission(PermissionSlug.CUSTOMIZATIONS_ACCESS) && (
              <SidebarMenuItem
                icon={<SlidersHorizontal className='h-4 w-4' />}
                label='Customizations'
                path='/customizations'
                variant='rogerdoc'
              />
            )}

            {hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
              <SidebarMenuItem
                icon={<FileText className='h-4 w-4' />}
                label='Forms Manager'
                path='/form-schemas'
                variant='rogerdoc'
              />
            )}

            <SidebarMenuItem
              icon={<BookOpen className='h-4 w-4' />}
              label='Resources'
              path='/resources'
              variant='rogerdoc'
            />

            {/* Divider between RogerDoc/Resources and EMR sections */}
            {!isSidebarCollapsed && (
              <div className='w-full h-px bg-border my-2' />
            )}

            {/* Currently not live / not being developed.
            QA should only be visible to super admins */}
            {rogerEmrEnabled &&
              hasPermission(PermissionSlug.QA_ACCESS) &&
              hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
                <SidebarMenuItem
                  icon={<CheckCircle className='h-4 w-4' />}
                  label='Quality Assurance'
                  path='/qa'
                />
              )}

            {rogerEmrEnabled &&
              hasAnyPermission([
                PermissionSlug.HOTBOX_ACCESS,
                PermissionSlug.EMR_MANAGE,
                PermissionSlug.QA_ACCESS,
              ]) && (
                <SidebarMenuItem
                  icon={<Inbox className='h-4 w-4' />}
                  label='Hotbox'
                  path='/hotbox'
                />
              )}

            {rogerEmrEnabled &&
              hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS) && (
                <SidebarMenuItem
                  icon={<Home className='h-4 w-4' />}
                  label='Dashboard'
                  path='/dashboard'
                />
              )}

            {hasPermission(PermissionSlug.INTAKE_ACCESS) && (
              <SidebarMenuItem
                icon={<FileText className='h-4 w-4' />}
                label='Intake Manager'
                path='/intake-manager'
              />
            )}

            {hasPermission(PermissionSlug.CODING_ACCESS) && (
              <SidebarMenuItem
                icon={<Code className='h-4 w-4' />}
                label='Medical Coding'
                path='/coding'
              />
            )}

            {rogerEmrEnabled &&
              hasAnyPermission([
                PermissionSlug.PATIENT_MANAGER,
                PermissionSlug.EMR_MANAGE,
              ]) && (
                <SidebarMenuItem
                  icon={<Users className='h-4 w-4' />}
                  label='Patient Manager'
                  path='/patients'
                />
              )}

            {rogerEmrEnabled && hasPermission(PermissionSlug.ORDER_MANAGER) && (
              <SidebarMenuItem
                icon={<ClipboardList className='h-4 w-4' />}
                label='Orders Manager'
                path='/orders'
              />
            )}

            {rogerEmrEnabled && hasPermission(PermissionSlug.OASIS_MANAGER) && (
              <SidebarMenuItem
                icon={<FileOutput className='h-4 w-4' />}
                label='OASIS Manager'
                path='/oasis-manager'
              />
            )}

            {rogerEmrEnabled && hasPermission(PermissionSlug.NOA_MANAGER) && (
              <SidebarMenuItem
                icon={<FileCheck className='h-4 w-4' />}
                label='NOA Manager'
                path='/noa-manager'
              />
            )}

            {rogerEmrEnabled && hasPermission(PermissionSlug.CAHPS_MANAGER) && (
              <SidebarMenuItem
                icon={<FileSpreadsheet className='h-4 w-4' />}
                label='CAHPS Manager'
                path='/cahps-manager'
              />
            )}

            {hasFeatureFlag(FeatureFlag.ADMIN_WORKSHEET_GOALS) &&
              hasPermission(PermissionSlug.GOALS_ADMIN_TAB) && (
                <SidebarMenuItem
                  icon={<Target className='h-4 w-4' />}
                  label='Worksheet Goals'
                  path='/worksheet-goals'
                />
              )}

            {rogerEmrEnabled && hasPayrollAccess && (
              <SidebarMenuItem
                icon={<Wallet className='h-4 w-4' />}
                label='Payroll Manager'
                path='/payroll-manager'
              />
            )}

            {hasAnyPermission([
              PermissionSlug.CLINICIAN_MANAGER,
              PermissionSlug.EMR_MANAGE,
            ]) && (
              <SidebarMenuItem
                icon={<Settings2 className='h-4 w-4' />}
                label='User Manager'
                path='/user-manager'
              />
            )}

            {hasReportsAccess && (
              <SidebarMenuItem
                icon={<FileBarChart className='h-4 w-4' />}
                label='Reports'
                path='/reports'
              />
            )}

            {rogerEmrEnabled &&
              hasPermission(PermissionSlug.BILLING_ACCESS) && (
                <SidebarMenuItem
                  icon={<DollarSign className='h-4 w-4' />}
                  label='Billing Manager'
                  path='/billing'
                />
              )}

            {rogerEmrEnabled &&
              // Auth Manager not rolled out to end user yet, only super admin can see it
              hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
                <SidebarMenuItem
                  icon={<BadgeCheck className='h-4 w-4' />}
                  label='Auth Manager'
                  path='/auth-manager'
                  activePath='/auth'
                />
              )}

            {visibleAdminSections(
              hasAnyPermission,
              rogerEmrEnabled,
              hasFeatureFlag
            ).length > 0 && (
              <SidebarMenuItem
                icon={<Shield className='h-4 w-4' />}
                label='Admin'
                path='/admin'
              />
            )}

            {hasAnyPermission([PermissionSlug.ADMIN_SUPER_USER]) && (
              <SidebarMenuItem
                icon={<KeyRound className='h-4 w-4' />}
                label='Roles & Permissions'
                path='/roles-manager'
              />
            )}

            {hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
              <SidebarMenuItem
                icon={<Shield className='h-4 w-4' />}
                label='Internal Admin'
                path='/internal-admin'
              />
            )}

            {hasPermission(PermissionSlug.IMPORTER_ACCESS) && (
              <SidebarMenuItem
                icon={<DatabaseBackup className='h-4 w-4' />}
                label='Importer'
                path='/importer'
              />
            )}

            {hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
              <SidebarMenuItem
                icon={<TestTube className='h-4 w-4' />}
                label='Testing'
                path='/testing'
              />
            )}

            {user?.agency?.system_name === 'Strawhat' &&
              hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
                <SidebarMenuItem
                  icon={<Wrench className='h-4 w-4' />}
                  label='Internal Tools'
                  path='/internal-tools'
                />
              )}
          </nav>
        </TooltipProvider>
      </aside>
    </SidebarPresentationContext.Provider>
  );
};

export default Sidebar;
